/**
 * Defines seasonal data and helper functions for seasonal and country-specific meal recommendations
 */

// Season definitions by month and hemisphere
export const SEASONS = {
  NORTHERN_HEMISPHERE: {
    WINTER: [12, 1, 2],
    SPRING: [3, 4, 5],
    SUMMER: [6, 7, 8],
    MONSOON: [7, 8], // Specific to some countries
    AUTUMN: [9, 10, 11],
    FALL: [9, 10, 11] // Alternative name for autumn
  },
  SOUTHERN_HEMISPHERE: {
    SUMMER: [12, 1, 2],
    AUTUMN: [3, 4, 5],
    FALL: [3, 4, 5],
    WINTER: [6, 7, 8],
    SPRING: [9, 10, 11]
  },
  TROPICAL: {
    WET: [5, 6, 7, 8, 9, 10], // Wet/rainy season (varies by country)
    DRY: [11, 12, 1, 2, 3, 4] // Dry season (varies by country)
  },
  INDIA: {
    WINTER: [12, 1, 2],
    SPRING: [3, 4],
    SUMMER: [5, 6],
    MONSOON: [7, 8, 9],
    AUTUMN: [10, 11]
  }
};

// Country-to-hemisphere mapping
export const COUNTRY_HEMISPHERE = {
  'India': 'INDIA', // Custom seasons
  'United States': 'NORTHERN_HEMISPHERE',
  'Canada': 'NORTHERN_HEMISPHERE',
  'United Kingdom': 'NORTHERN_HEMISPHERE',
  'France': 'NORTHERN_HEMISPHERE',
  'Germany': 'NORTHERN_HEMISPHERE',
  'Italy': 'NORTHERN_HEMISPHERE',
  'Spain': 'NORTHERN_HEMISPHERE',
  'Japan': 'NORTHERN_HEMISPHERE',
  'China': 'NORTHERN_HEMISPHERE',
  'Russia': 'NORTHERN_HEMISPHERE',
  'Australia': 'SOUTHERN_HEMISPHERE',
  'New Zealand': 'SOUTHERN_HEMISPHERE',
  'Brazil': 'SOUTHERN_HEMISPHERE',
  'Argentina': 'SOUTHERN_HEMISPHERE',
  'South Africa': 'SOUTHERN_HEMISPHERE',
  'Chile': 'SOUTHERN_HEMISPHERE',
  'Singapore': 'TROPICAL',
  'Malaysia': 'TROPICAL',
  'Indonesia': 'TROPICAL',
  'Thailand': 'TROPICAL',
  'Vietnam': 'TROPICAL',
  'Philippines': 'TROPICAL',
  'Mexico': 'NORTHERN_HEMISPHERE',
  'UAE': 'NORTHERN_HEMISPHERE'
};

// Default for countries not listed
const DEFAULT_HEMISPHERE = 'NORTHERN_HEMISPHERE';

/**
 * Get the current season based on month and country
 * @param {number} month - Current month (1-12)
 * @param {string} country - Country name
 * @returns {string} - The current season
 */
export const getSeasonByMonthAndCountry = (month, country) => {
  // Determine the hemisphere or custom seasonal system based on country
  const hemisphere = COUNTRY_HEMISPHERE[country] || DEFAULT_HEMISPHERE;
  
  // Get the seasonal definitions for this hemisphere/country
  const seasonDefs = SEASONS[hemisphere];
  
  // Find which season the month belongs to
  for (const [season, months] of Object.entries(seasonDefs)) {
    if (months.includes(month)) {
      return season.toLowerCase();
    }
  }
  
  // Fallback to a default season if not found
  return 'any';
};

/**
 * Get seasonal food recommendations based on country and season
 * @param {string} country - Country name
 * @param {string} season - Current season
 * @returns {Object} - Seasonal recommendations
 */
export const getSeasonalRecommendations = (country, season) => {
  const recommendations = {
    ingredients: [],
    dishes: [],
    cuisineStyles: []
  };
  
  // India-specific seasonal recommendations
  if (country === 'India') {
    switch (season) {
      case 'winter':
        recommendations.ingredients = ['Carrots', 'Spinach', 'Mustard Greens', 'Sweet Potatoes', 'Beetroot', 'Cauliflower'];
        recommendations.dishes = ['Sarson Da Saag', 'Makki Di Roti', 'Gajar Halwa', 'Undhiyu', 'Til Ladoo', 'Hot Chai', 'Pongal'];
        recommendations.cuisineStyles = ['north-indian', 'punjabi', 'gujarati'];
        break;
      case 'summer':
        recommendations.ingredients = ['Watermelon', 'Mango', 'Cucumber', 'Mint', 'Yogurt', 'Lemon'];
        recommendations.dishes = ['Aam Panna', 'Lassi', 'Chaas', 'Nimbu Pani', 'Cucumber Raita', 'Curd Rice', 'Sattu Drinks', 'Mango Desserts'];
        recommendations.cuisineStyles = ['south-indian', 'light', 'refreshing'];
        break;
      case 'monsoon':
        recommendations.ingredients = ['Corn', 'Turmeric', 'Ginger', 'Pakora Veggies', 'Tamarind'];
        recommendations.dishes = ['Pakoras', 'Bhajias', 'Masala Chai', 'Khichdi', 'Kadhi', 'Samosa', 'Corn Dishes'];
        recommendations.cuisineStyles = ['street-food', 'fried', 'warming'];
        break;
      case 'spring':
        recommendations.ingredients = ['Green Peas', 'Fenugreek', 'Strawberries', 'Tender Vegetables'];
        recommendations.dishes = ['Aam Ras', 'Basant Paneer', 'Methi Dishes', 'Strawberry Desserts', 'Thandai'];
        recommendations.cuisineStyles = ['festive', 'light', 'holi-special'];
        break;
      case 'autumn':
        recommendations.ingredients = ['Pumpkin', 'Apples', 'Pomegranate', 'Nuts', 'Root Vegetables'];
        recommendations.dishes = ['Kadhi Chawal', 'Pumpkin Dishes', 'Sabudana Khichdi', 'Upma', 'Festive Sweets'];
        recommendations.cuisineStyles = ['festive', 'balanced', 'diwali-special'];
        break;
      default:
        break;
    }
  } 
  // US/Western countries
  else if (['United States', 'Canada', 'United Kingdom', 'Australia'].includes(country)) {
    switch (season) {
      case 'winter':
        recommendations.ingredients = ['Root Vegetables', 'Citrus Fruits', 'Winter Squash', 'Brussels Sprouts', 'Kale'];
        recommendations.dishes = ['Soups', 'Stews', 'Pot Roasts', 'Hearty Pasta', 'Hot Chocolate', 'Apple Pie'];
        recommendations.cuisineStyles = ['hearty', 'comfort food', 'baked'];
        break;
      case 'summer':
        recommendations.ingredients = ['Corn', 'Tomatoes', 'Berries', 'Watermelon', 'Summer Squash', 'Peaches'];
        recommendations.dishes = ['BBQ', 'Grilled Foods', 'Salads', 'Ice Cream', 'Corn on the Cob', 'Fruit Pies'];
        recommendations.cuisineStyles = ['grilled', 'fresh', 'cold'];
        break;
      case 'spring':
        recommendations.ingredients = ['Asparagus', 'Peas', 'Artichokes', 'Spring Onions', 'Strawberries'];
        recommendations.dishes = ['Spring Vegetable Pasta', 'Asparagus Dishes', 'Light Soups', 'Salads', 'Quiche'];
        recommendations.cuisineStyles = ['light', 'fresh', 'easter-themed'];
        break;
      case 'autumn':
      case 'fall':
        recommendations.ingredients = ['Pumpkin', 'Apples', 'Sweet Potatoes', 'Brussels Sprouts', 'Cranberries'];
        recommendations.dishes = ['Pumpkin Pie', 'Turkey Dinner', 'Apple Cider', 'Roasted Vegetables', 'Chili'];
        recommendations.cuisineStyles = ['thanksgiving', 'harvest', 'roasted'];
        break;
      default:
        break;
    }
  }
  // Asian countries excluding India
  else if (['Japan', 'China', 'Singapore'].includes(country)) {
    switch (season) {
      case 'winter':
        recommendations.ingredients = ['Daikon Radish', 'Chinese Cabbage', 'Mushrooms', 'Citrus', 'Root Vegetables'];
        recommendations.dishes = ['Hot Pot', 'Ramen', 'Sukiyaki', 'Oden', 'Steamed Dumplings', 'Hot Tofu'];
        recommendations.cuisineStyles = ['warming', 'fermented', 'soupy'];
        break;
      case 'summer':
        recommendations.ingredients = ['Bitter Melon', 'Eggplant', 'Cucumber', 'Watermelon', 'Soy'];
        recommendations.dishes = ['Cold Noodles', 'Hiyashi Chuka', 'Somen', 'Chilled Tofu', 'Grilled Eel'];
        recommendations.cuisineStyles = ['cooling', 'light', 'grilled'];
        break;
      case 'spring':
        recommendations.ingredients = ['Bamboo Shoots', 'Spring Onions', 'Greens', 'Cherry Blossoms (Japan)'];
        recommendations.dishes = ['Spring Rolls', 'Tempura', 'Sakura Mochi', 'Stir-Fried Greens'];
        recommendations.cuisineStyles = ['fresh', 'steamed', 'cherry blossom'];
        break;
      case 'autumn':
        recommendations.ingredients = ['Chestnuts', 'Persimmons', 'Matsutake Mushrooms', 'Sweet Potatoes', 'Taro'];
        recommendations.dishes = ['Chestnut Rice', 'Autumn Moon Cakes', 'Mushroom Hot Pot', 'Claypot Rice'];
        recommendations.cuisineStyles = ['seasonal', 'festival', 'harvest'];
        break;
      case 'wet':
      case 'dry':
        recommendations.ingredients = ['Rice', 'Tropical Fruits', 'Seafood', 'Local Vegetables'];
        recommendations.dishes = ['Laksa', 'Satay', 'Stir-Fries', 'Tropical Fruit Desserts'];
        recommendations.cuisineStyles = ['spicy', 'tropical', 'street food'];
        break;
      default:
        break;
    }
  }
  
  // Default recommendations for any season/country not covered
  if (recommendations.ingredients.length === 0) {
    recommendations.ingredients = ['Seasonal Vegetables', 'Local Fruits', 'Regional Specialties'];
    recommendations.dishes = ['Popular Local Dishes', 'Traditional Recipes', 'Regional Specialties'];
    recommendations.cuisineStyles = ['traditional', 'local', 'seasonal'];
  }
  
  return recommendations;
};
